import Hls from 'hls.js';

/**
 * Ultra-simple HLS manager - match MP4 simplicity
 */
class HLSManager {
    constructor() {
        this.hls = null;
        this.video = null;
        this.currentUrl = null;
    }

    static isSupported() {
        return Hls.isSupported();
    }

    static hasNativeSupport(video) {
        if (!video) return false;
        return video.canPlayType('application/vnd.apple.mpegurl') !== '';
    }

    /**
     * Initialize HLS - as simple as possible
     */
    init(video, url) {
        if (!video || !url) {
            console.error('[HLSManager] Invalid video or url');
            return false;
        }

        // Already initialized for this URL
        if (this.currentUrl === url && this.hls && !this.hls.destroyed) {
            return true;
        }

        // Check native support (should be handled by caller, but safety check)
        if (HLSManager.hasNativeSupport(video)) {
            console.log('[HLSManager] Native HLS support detected, caller should handle');
            return false;
        }

        // Check HLS.js support
        if (!HLSManager.isSupported()) {
            console.error('[HLSManager] HLS.js not supported in this browser');
            return false;
        }

        // Clean up any existing instance
        this.destroy();

        this.video = video;
        this.currentUrl = url;

        // Create HLS instance with minimal config
        this.hls = new Hls({
            autoStartLoad: true,
            maxBufferLength: 10,
            maxMaxBufferLength: 600,
            backBufferLength: 30,
            enableWorker: true,
        });

        // Simple event handlers
        this.setupEventHandlers();

        // Attach and load - that's it!
        this.hls.attachMedia(video);
        this.hls.loadSource(url);

        console.log('[HLSManager] HLS initialized');
        return true;
    }

    /**
     * Minimal event handlers - just log errors
     */
    setupEventHandlers() {
        if (!this.hls) return;

        this.hls.on(Hls.Events.ERROR, (event, data) => {
            console.error('[HLSManager] HLS error:', {
                type: data.type,
                details: data.details,
                fatal: data.fatal,
            });

            // Only handle fatal errors with one simple recovery attempt
            if (data.fatal) {
                if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
                    console.log('[HLSManager] Media error - attempting recovery');
                    this.hls.recoverMediaError();
                } else if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
                    console.log('[HLSManager] Network error - reloading');
                    this.hls.startLoad();
                } else {
                    console.error('[HLSManager] Fatal error - cannot recover');
                }
            }
        });
    }

    /**
     * Clean up
     */
    destroy() {
        if (this.hls) {
            this.hls.destroy();
            this.hls = null;
        }
        this.video = null;
        this.currentUrl = null;
    }
}

export default HLSManager;